import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, Brain, TrendingUp, AlertTriangle, Lightbulb,
  Moon, Utensils, Cloud, Heart, Loader2, RefreshCw, Sparkles, ShieldOff
} from 'lucide-react';
import { toast } from 'sonner';
import AIConsentDialog, { useAIConsent } from '../components/AIConsentDialog';

const SmartCorrelations = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const [correlations, setCorrelations] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAI, setLoadingAI] = useState(false);
  
  // AI Consent
  const { 
    hasConsent, 
    showConsentDialog, 
    setShowConsentDialog,
    acceptConsent, 
    declineConsent 
  } = useAIConsent();

  useEffect(() => {
    if (hasConsent === true) {
      fetchCorrelations();
    } else if (hasConsent === false) {
      setLoading(false);
    } else {
      setShowConsentDialog(true);
      setLoading(false);
    }
  }, [hasConsent]);

  const fetchCorrelations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/smart-correlations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorrelations(response.data);
    } catch (error) {
      console.error('Error fetching correlations:', error);
      toast.error('Failed to load correlations');
    } finally {
      setLoading(false);
    }
  };

  const fetchAIAnalysis = async () => {
    if (hasConsent !== true) {
      setShowConsentDialog(true);
      return;
    }
    setLoadingAI(true);
    try {
      const response = await axios.get(`${API}/smart-correlations/ai-analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiAnalysis(response.data);
      toast.success('AI analysis complete!');
    } catch (error) {
      console.error('Error fetching AI analysis:', error);
      toast.error('Failed to generate AI analysis');
    } finally {
      setLoadingAI(false);
    }
  };

  const getTriggerIcon = (trigger) => {
    const lower = trigger.toLowerCase();
    if (lower.includes('sleep')) return <Moon className="w-5 h-5 text-indigo-500" />;
    if (lower.includes('food') || lower.includes('diet')) return <Utensils className="w-5 h-5 text-green-500" />;
    if (lower.includes('weather') || lower.includes('pressure') || lower.includes('humidity')) return <Cloud className="w-5 h-5 text-sky-500" />;
    if (lower.includes('period') || lower.includes('menstrual') || lower.includes('cycle')) return <Heart className="w-5 h-5 text-pink-500" />;
    return <AlertTriangle className="w-5 h-5 text-amber-500" />;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence === 'high') return 'bg-green-100 text-green-700';
    if (confidence === 'medium') return 'bg-amber-100 text-amber-700';
    return 'bg-stone-100 text-stone-600';
  };

  const getFlareRateColor = (rate) => {
    if (rate >= 60) return 'text-red-600';
    if (rate >= 40) return 'text-amber-600';
    if (rate >= 25) return 'text-amber-500';
    return 'text-green-600';
  };

  return (
    <div className="space-y-8" data-testid="smart-correlations">
      {/* AI Consent Dialog */}
      <AIConsentDialog
        isOpen={showConsentDialog}
        onAccept={acceptConsent}
        onDecline={declineConsent}
        feature="Smart Correlations"
      />

      {/* Consent Declined State */}
      {hasConsent === false && (
        <Card className="bg-white rounded-xl border border-stone-100 shadow-sm p-8">
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldOff className="w-8 h-8 text-stone-400" />
            </div>
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">
              AI Features Disabled
            </h2>
            <p className="text-[#57534E] mb-4">
              Smart Correlations requires AI data sharing. To use this feature, please accept data sharing with OpenAI.
            </p>
            <Button
              onClick={() => setShowConsentDialog(true)}
              className="bg-primary hover:bg-primary/90"
            >
              Review Data Sharing Settings
            </Button>
          </div>
        </Card>
      )}

      {hasConsent === true && (
        <>
      {!embedded && (
        <Button 
          onClick={() => navigate('/dashboard')} 
          variant="ghost" 
          className="text-primary hover:text-primary/80"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </Button>
      )}

      {/* Header */}
      {!embedded && (
        <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 rounded-xl p-8">
          <div className="flex items-center gap-4 mb-2">
            <Brain className="w-10 h-10 text-purple-600" strokeWidth={1.5} />
            <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">
              Smart Correlations
            </h1>
          </div>
          <p className="text-[#57534E]">
            AI-powered analysis of all your tracking data to identify triggers and patterns
          </p>
        </div>
      )}

      {embedded && (
        <div className="flex items-center gap-3 mb-4">
          <Brain className="w-6 h-6 text-purple-600" />
          <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">Smart Correlations</h2>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-purple-500 animate-spin" />
          <p className="text-[#57534E]">Analyzing your data...</p>
        </div>
      ) : !correlations?.has_analysis ? (
        <Card className="p-8 text-center">
          <Brain className="w-16 h-16 mx-auto mb-4 text-stone-300" />
          <h2 className="text-xl font-bold text-[#2D2D2D] mb-2">More Data Needed</h2>
          <p className="text-[#57534E] mb-4">{correlations?.message || 'Keep tracking to enable correlation analysis!'}</p>
          
          {correlations?.data_status && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6 text-sm">
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-semibold text-[#2D2D2D]">{correlations.data_status.symptoms}</p>
                <p className="text-[#A8A29E]">Symptom entries</p>
              </div>
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-semibold text-[#2D2D2D]">{correlations.data_status.flares}</p>
                <p className="text-[#A8A29E]">Flare entries</p>
              </div>
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-semibold text-[#2D2D2D]">{correlations.data_status.sleep}</p>
                <p className="text-[#A8A29E]">Sleep entries</p>
              </div>
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-semibold text-[#2D2D2D]">{correlations.data_status.food}</p>
                <p className="text-[#A8A29E]">Food entries</p>
              </div>
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-semibold text-[#2D2D2D]">{correlations.data_status.weather}</p>
                <p className="text-[#A8A29E]">Weather entries</p>
              </div>
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-semibold text-[#2D2D2D]">{correlations.data_status.cycle}</p>
                <p className="text-[#A8A29E]">Cycle entries</p>
              </div>
            </div>
          )}
        </Card>
      ) : (
        <>
          {/* Data Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 text-center">
              <TrendingUp className="w-6 h-6 mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold text-[#2D2D2D]">{correlations.total_flares_analyzed}</p>
              <p className="text-sm text-[#57534E]">Flares Analyzed</p>
            </Card>
            <Card className="p-4 text-center">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-amber-500" />
              <p className="text-2xl font-bold text-[#2D2D2D]">{correlations.triggers?.length || 0}</p>
              <p className="text-sm text-[#57534E]">Triggers Found</p>
            </Card>
            <Card className="p-4 text-center">
              <Lightbulb className="w-6 h-6 mx-auto mb-2 text-yellow-500" />
              <p className="text-2xl font-bold text-[#2D2D2D]">{correlations.insights?.length || 0}</p>
              <p className="text-sm text-[#57534E]">Insights</p>
            </Card>
            <Card className="p-4 text-center">
              <RefreshCw className="w-6 h-6 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold text-[#2D2D2D]">{correlations.analysis_period_days}</p>
              <p className="text-sm text-[#57534E]">Days Analyzed</p>
            </Card>
          </div>

          {/* AI Analysis Section */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[#2D2D2D] flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                AI-Powered Analysis
              </h2>
              <Button
                onClick={fetchAIAnalysis}
                disabled={loadingAI}
                variant="outline"
                className="text-purple-600 border-purple-200"
                data-testid="get-ai-analysis-btn"
              >
                {loadingAI ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Brain className="w-4 h-4 mr-2" />
                    Generate AI Insights
                  </>
                )}
              </Button>
            </div>
            
            {aiAnalysis?.ai_analysis ? (
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-[#57534E] whitespace-pre-wrap">{aiAnalysis.ai_analysis}</p>
              </div>
            ) : (
              <p className="text-[#A8A29E] text-center py-4">
                Click "Generate AI Insights" for personalized analysis based on your data
              </p>
            )}
          </Card>

          {/* Triggers List */}
          {correlations.triggers && correlations.triggers.length > 0 && (
            <Card className="p-6">
              <h2 className="text-xl font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Identified Triggers
              </h2>
              
              <div className="space-y-4">
                {correlations.triggers.map((trigger, index) => (
                  <div 
                    key={index} 
                    className="p-4 bg-stone-50 rounded-xl border-l-4 border-amber-400"
                    data-testid={`trigger-${index}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {getTriggerIcon(trigger.trigger)}
                        <div>
                          <p className="font-bold text-[#2D2D2D]">{trigger.trigger}</p>
                          <p className="text-sm text-[#57534E]">{trigger.recommendation}</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${getFlareRateColor(trigger.flare_rate)}`}>
                          {trigger.flare_rate}%
                        </p>
                        <p className="text-xs text-[#A8A29E]">flare rate</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(trigger.confidence)}`}>
                        {trigger.confidence} confidence
                      </span>
                      <span className="text-xs text-[#A8A29E]">
                        {trigger.sample_size} instances
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Insights List */}
          {correlations.insights && correlations.insights.length > 0 && (
            <Card className="p-6">
              <h2 className="text-xl font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-600" />
                Key Insights
              </h2>
              
              <div className="space-y-4">
                {correlations.insights.map((insight, index) => (
                  <div 
                    key={index}
                    className="p-4 bg-yellow-50 rounded-xl border-l-4 border-yellow-400"
                  >
                    <p className="font-semibold text-[#2D2D2D] mb-1">{insight.message}</p>
                    {insight.action && (
                      <p className="text-sm text-[#57534E]">
                        <span className="font-medium">Action:</span> {insight.action}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Last Analyzed */}
          <p className="text-center text-sm text-[#A8A29E]">
            Analysis based on {correlations.analysis_period_days} days of data • 
            Last updated: {new Date(correlations.analyzed_at).toLocaleString()}
          </p>
        </>
      )}

      {/* Refresh Button */}
      {hasConsent === true && (
      <div className="text-center">
        <Button
          onClick={fetchCorrelations}
          variant="outline"
          disabled={loading}
          data-testid="refresh-correlations-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh Analysis
        </Button>
      </div>
      )}
      </>
      )}
    </div>
  );
};

export default SmartCorrelations;
